import React, { useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import { GlobalForm } from '@/index';
import type { GlobalFormRef } from '@/components/GlobalForm/GlobalForm';
import type { FormField } from '@/types/form';
import type { UserResponse, CreateUserRequest, UpdateUserRequest } from '@/types/api';
import { usersAPI } from '@/services/api/users';

interface UserFormProps {
  user?: UserResponse;
  onSuccess?: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, onSuccess }) => {
  const formRef = useRef<GlobalFormRef>(null);
  const [loading, setLoading] = useState(false);
  const isEdit = !!user;

  // 角色选项
  const roleOptions = [
    { value: 'admin', label: '管理员' },
    { value: 'publisher', label: '发布者' },
    { value: 'viewer', label: '只读' },
  ];

  // 状态选项
  const statusOptions = [
    { value: 'active', label: '活跃' },
    { value: 'disabled', label: '禁用' },
  ];

  // 初始化表单
  useEffect(() => {
    if (user && formRef.current) {
      formRef.current.setFieldsValue({
        username: user.username,
        name: user.name,
        email: user.email,
        phone: user.phone,
        bio: user.bio,
        department: user.department,
        role: user.role,
        status: user.status,
      });
    }
  }, [user]);

  // 处理提交
  const handleSubmit = async (values: any) => {
    setLoading(true);
    
    try {
      if (isEdit && user) {
        // 更新用户
        const updateData: UpdateUserRequest = {
          username: values.username,
          name: values.name,
          email: values.email,
          phone: values.phone,
          bio: values.bio,
          department: values.department,
          role: values.role,
          status: values.status,
        };
        
        await usersAPI.updateUser(user.id, updateData);
        message.success('用户更新成功');
      } else {
        // 创建用户
        const createData: CreateUserRequest = {
          username: values.username,
          name: values.name,
          email: values.email,
          password: values.password,
          role: values.role,
          phone: values.phone,
          bio: values.bio,
          department: values.department,
        };
        
        await usersAPI.createUser(createData);
        message.success('用户添加成功');
      }
      
      onSuccess?.();
    } catch (error: any) {
      console.error('用户操作失败:', error);
      // 全局错误拦截器已经处理了错误提示，这里不需要重复显示
      // 只需要记录日志即可
      // 不调用 onSuccess，因为操作失败了
      return;
    } finally {
      setLoading(false);
    }
  };

  // 表单字段配置
  const formFields: FormField[] = [
    {
      name: 'username',
      label: '用户名',
      type: 'input',
      placeholder: '输入用户名',
      disabled: isEdit, // 编辑时不允许修改用户名
      rules: [
        { required: true, message: '请输入用户名' },
        { min: 3, message: '用户名至少3个字符' },
        { max: 20, message: '用户名最多20个字符' },
      ],
      colSpan: 12,
    },
    {
      name: 'name',
      label: '姓名',
      type: 'input',
      placeholder: '输入真实姓名',
      rules: [
        { required: true, message: '请输入姓名' },
        { min: 2, message: '姓名至少2个字符' },
        { max: 20, message: '姓名最多20个字符' },
      ],
      colSpan: 12,
    },
    {
      name: 'email',
      label: '邮箱',
      type: 'input',
      placeholder: '输入邮箱地址',
      rules: [
        { required: true, message: '请输入邮箱' },
        { type: 'email', message: '请输入有效的邮箱地址' },
      ],
      colSpan: 12,
    },
    {
      name: 'phone',
      label: '手机号',
      type: 'input',
      placeholder: '输入手机号码',
      rules: [
        { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' },
      ],
      colSpan: 12,
    },
    {
      name: 'department',
      label: '部门',
      type: 'input',
      placeholder: '输入所属部门',
      colSpan: 12,
    },
    {
      name: 'role',
      label: '角色',
      type: 'select',
      placeholder: '选择角色',
      options: roleOptions,
      rules: [{ required: true, message: '请选择角色' }],
      colSpan: 12,
    },
    // 状态字段 - 仅在编辑时显示
    ...(isEdit ? [{
      name: 'status',
      label: '状态',
      type: 'select',
      placeholder: '选择状态',
      options: statusOptions,
      rules: [{ required: true, message: '请选择状态' }],
      colSpan: 12,
    } as FormField] : []),
    // 初始密码 - 仅在添加用户时显示
    ...(isEdit ? [] : [{
      name: 'password',
      label: '初始密码',
      type: 'password',
      placeholder: '输入初始密码 (至少8位)',
      extra: '用户首次登录后可以修改密码',
      rules: [
        { required: true, message: '请输入初始密码' },
        { min: 8, message: '密码至少8位' },
      ],
      colSpan: 12,
    } as FormField]),
    {
      name: 'bio',
      label: '个人简介',
      type: 'textarea',
      placeholder: '输入个人简介（可选）',
      rows: 3,
      colSpan: 24,
    },
  ];

  return (
    <div className="p-6">
      <GlobalForm
        ref={formRef}
        fields={formFields}
        layout="vertical"
        columns={1}
        loading={loading}
        showSubmit={true}
        showReset={false}
        submitText={isEdit ? '更新' : '添加'}
        onFinish={handleSubmit}
      />
      
      {/* 状态说明 */}
      {isEdit && (
        <div className="text-sm text-gray-500 mt-4">
          禁用后该用户将无法登录系统
        </div>
      )}
    </div>
  );
};

export default UserForm;
